import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import Seo from "@/components/Seo";
import { ArrowRight, MapPin, Sparkles, FileText, Building2, Waves, Star } from "lucide-react";

const PROJECTS = [
  "ADU", "Deck", "Fence", "Pool", "Remodel", "Garage", "Shed",
  "Solar", "Patio", "Addition", "Retaining Wall", "Kitchen", "Bathroom"
];

const CITIES = [
  "City of San Diego", "Chula Vista", "Oceanside", "Carlsbad", "Encinitas",
  "Escondido", "El Cajon", "La Mesa", "Santee", "Poway", "Vista",
  "San Marcos", "National City", "Lemon Grove", "Imperial Beach",
  "Solana Beach", "Del Mar", "Coronado", "SD County Unincorporated"
];

export default function Landing() {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [selected, setSelected] = useState(null);

  const handleStart = () => {
    if (selected) {
      const params = new URLSearchParams({
        address: selected.full_address,
        city: selected.city || "",
        county: selected.county || "",
        lat: selected.lat || "",
        lon: selected.lon || "",
      });
      navigate(`/new?${params.toString()}`);
    } else {
      const q = address ? `?address=${encodeURIComponent(address)}` : "";
      navigate(`/new${q}`);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="BuildWise AI — San Diego Building Permit Reports & Feasibility Analysis"
        description="AI-powered building permit feasibility, cost estimates, and timeline reports for all 19 San Diego County jurisdictions. Know your permits, fees, and inspections before you build."
        path="/"
      />
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-start">
          <div className="md:col-span-7">
            <div className="label-overline mb-6" data-testid="hero-tag">San Diego County · AI-Powered</div>
            <h1
              className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
              style={{ color: "var(--bw-text-primary)" }}
              data-testid="hero-headline"
            >
              Know Before<br/>
              You Build.
            </h1>
            <p
              className="mt-8 text-lg md:text-xl max-w-xl leading-relaxed"
              style={{ color: "var(--bw-text-secondary)" }}
              data-testid="hero-subheadline"
            >
              BuildWise AI interviews you, checks your jurisdiction, and delivers a permit-ready
              feasibility report — costs, timelines, inspections, and all — for any San Diego property.
            </p>

            <div className="mt-10 max-w-xl">
              <label className="label-overline block mb-3">Enter property address</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={setSelected}
                variant="mega"
                placeholder="1234 Sunset Cliffs Blvd, San Diego"
                testid="hero-address"
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="btn-primary" onClick={handleStart} data-testid="hero-start-btn">
                  Start Free Analysis <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <Link to="/signup" className="btn-outline" data-testid="hero-signup-btn">
                  Create Account
                </Link>
              </div>
              <p className="mt-4 text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
                Free summary · No credit card required · All 19 SD jurisdictions
              </p>
            </div>
          </div>

          {/* Bento imagery */}
          <div className="md:col-span-5 grid grid-cols-6 gap-3">
            <div className="col-span-6 aspect-[4/3] overflow-hidden border relative group" style={{ borderColor: "var(--bw-border)" }}>
              <img
                src="https://images.unsplash.com/photo-1583428043200-31c33d3d3937?w=1200&q=80&auto=format&fit=crop"
                alt="La Jolla San Diego coastline"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80&auto=format&fit=crop"; }}
              />
              <div className="absolute bottom-3 left-3 px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-white bg-black/40 backdrop-blur-sm">
                La Jolla · Pacific Coast
              </div>
            </div>
            <div className="col-span-3 aspect-square overflow-hidden border" style={{ borderColor: "var(--bw-border)" }}>
              <img
                src="https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&q=80&auto=format&fit=crop"
                alt="Sunset Cliffs ocean view"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=800&q=80&auto=format&fit=crop"; }}
              />
            </div>
            <div className="col-span-3 aspect-square flex items-center justify-center" style={{ backgroundColor: "var(--bw-brand)" }}>
              <div className="text-center p-4">
                <Waves className="w-10 h-10 mx-auto mb-2 text-white" strokeWidth={1.5} />
                <div className="font-display font-black text-3xl text-white">19</div>
                <div className="text-white/80 text-xs uppercase tracking-widest mt-1">Cities Covered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="label-overline mb-4">How BuildWise Works</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight max-w-2xl" style={{ color: "var(--bw-text-primary)" }}>
            From address to approved plan — with clarity at every step.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              { icon: MapPin, title: "01 · Pinpoint", body: "Enter your address. We auto-detect your city or unincorporated county jurisdiction." },
              { icon: Sparkles, title: "02 · Interview", body: "A Claude Sonnet-powered AI asks the right questions about your specific project." },
              { icon: FileText, title: "03 · Report", body: "Get a free summary. Unlock the full permit checklist, fees, timelines & inspections." },
            ].map((s) => (
              <div key={s.title} className="bw-card p-8" data-testid={`how-step-${s.title.split(' ')[0]}`}>
                <s.icon className="w-8 h-8 mb-6" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                <div className="font-display font-bold text-xl mb-3" style={{ color: "var(--bw-text-primary)" }}>{s.title}</div>
                <p className="text-base leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT TYPES */}
      <section style={{ backgroundColor: "var(--bw-bg-tertiary)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="label-overline mb-4">Projects We Cover</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight max-w-2xl mb-12" style={{ color: "var(--bw-text-primary)" }}>
            13 project types, permit-ready.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {PROJECTS.map((p) => (
              <div key={p} className="project-tile" data-testid={`project-tile-${p.toLowerCase().replace(/\s+/g, '-')}`}>
                <Building2 className="w-6 h-6" strokeWidth={1.5} />
                <span className="font-semibold">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="label-overline mb-4">Simple, Transparent Pricing</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight max-w-2xl mb-12" style={{ color: "var(--bw-text-primary)" }}>
            Free to start. Unlock the details when you're ready.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* FREE */}
            <div className="bw-card p-8 flex flex-col" data-testid="pricing-free">
              <div className="label-overline">Free</div>
              <div className="metric-big mt-4">$0</div>
              <p className="mt-2 text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-text-tertiary)" }}>Explorer</p>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
                Get a fast, honest feel for your project before spending a dollar.
              </p>
              <ul className="mt-5 space-y-2 text-sm flex-1" style={{ color: "var(--bw-text-primary)" }}>
                <li>· AI address & jurisdiction detection</li>
                <li>· Full AI interview (~5 questions)</li>
                <li>· High-level feasibility summary</li>
                <li>· Cost & timeline ballpark</li>
              </ul>
              <Link to="/signup" className="btn-outline mt-8 w-full inline-flex" data-testid="pricing-free-cta">
                Start Free
              </Link>
            </div>

            {/* ESSENTIAL */}
            <div className="bw-card p-8 flex flex-col" data-testid="pricing-essential">
              <div className="label-overline">Essential</div>
              <div className="metric-big mt-4">$49</div>
              <p className="mt-2 text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-text-tertiary)" }}>Homeowner</p>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
                Everything you need to walk into your city's counter prepared.
              </p>
              <ul className="mt-5 space-y-2 text-sm flex-1" style={{ color: "var(--bw-text-primary)" }}>
                <li>· Full permit checklist</li>
                <li>· Itemized fee estimate</li>
                <li>· Phase-by-phase timeline</li>
                <li>· Inspection roadmap</li>
                <li>· Direct links to official forms</li>
              </ul>
              <Link to="/signup" className="btn-primary mt-8 w-full inline-flex" data-testid="pricing-essential-cta">
                Get Essential
              </Link>
            </div>

            {/* PROFESSIONAL */}
            <div className="bw-card p-8 flex flex-col relative" style={{ borderColor: "var(--bw-accent)", borderWidth: 2 }} data-testid="pricing-pro">
              <div className="absolute -top-3 left-6 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-white" style={{ backgroundColor: "var(--bw-accent)" }}>
                Most Popular
              </div>
              <div className="label-overline" style={{ color: "var(--bw-accent)" }}>Professional</div>
              <div className="metric-big mt-4">$99</div>
              <p className="mt-2 text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-text-tertiary)" }}>Contractor · Developer</p>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
                Deep, jurisdiction-specific guidance built to move a project fast.
              </p>
              <ul className="mt-5 space-y-2 text-sm flex-1" style={{ color: "var(--bw-text-primary)" }}>
                <li>· Everything in Essential</li>
                <li>· Extensive AI expert guidance</li>
                <li>· Every required document listed</li>
                <li>· Downloadable full report</li>
                <li>· Priority AI response</li>
              </ul>
              <Link to="/signup" className="btn-accent mt-8 w-full inline-flex" data-testid="pricing-pro-cta">
                Get Professional
              </Link>
            </div>

            {/* LIVE CHAT ADD-ON */}
            <div className="bw-card p-8 flex flex-col" style={{ backgroundColor: "var(--bw-bg-tertiary)" }} data-testid="pricing-livechat">
              <div className="label-overline">Live AI Chat</div>
              <div className="metric-big mt-4">$9.99</div>
              <p className="mt-2 text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-text-tertiary)" }}>30-day pass · Add-on</p>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
                Keep asking questions long after the summary. Your always-on permit advisor.
              </p>
              <ul className="mt-5 space-y-2 text-sm flex-1" style={{ color: "var(--bw-text-primary)" }}>
                <li>· Unlimited follow-up questions</li>
                <li>· Works across all your projects</li>
                <li>· 30 days, cancel anytime</li>
                <li>· Best paired with any report</li>
              </ul>
              <Link to="/signup" className="btn-outline mt-8 w-full inline-flex" data-testid="pricing-livechat-cta">
                Add Live Chat
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED REVIEWS */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)", backgroundColor: "var(--bw-bg-tertiary)" }} data-testid="featured-reviews-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="label-overline mb-3">Loved by Local Pros</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight max-w-2xl" style={{ color: "var(--bw-text-primary)" }}>
                4.9 out of 5, across 247 verified contractors.
              </h2>
            </div>
            <Link to="/reviews" className="btn-outline text-sm" data-testid="see-all-reviews">
              See all 247 reviews
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: "md", initials: "MD", name: "Marco Delgado", role: "General Contractor · 22 yrs", company: "Coastal Build & Design", accent: "#1E4B66",
                quote: "BuildWise cut my pre-application prep from 3 hours to 20 minutes. Chula Vista fee estimates were off by less than 4% on my last three ADUs.",
                project: "ADU · Chula Vista" },
              { id: "pk", initials: "PK", name: "Priya Kaur", role: "ADU Specialist · Licensed B-1", company: "Kaur ADU Builders", accent: "#E86A33",
                quote: "The pre-approved plans lookup is a lifesaver. I've closed 2 extra jobs this quarter just by setting expectations correctly.",
                project: "ADU · Encinitas" },
              { id: "ds", initials: "DS", name: "Diane & Rob Sullivan", role: "Homeowner", company: "Concierge customer", accent: "#5C6E7C",
                quote: "Concierge handled everything with Solana Beach — three revisions, one appeal, and a full permit in 11 weeks. Worth every penny of the $299.",
                project: "Addition · Solana Beach" },
            ].map((r) => (
              <div key={r.id} className="bw-card p-6" data-testid={`featured-review-${r.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 flex items-center justify-center font-display font-bold text-white text-sm" style={{ backgroundColor: r.accent }}>
                    {r.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base leading-tight" style={{ color: "var(--bw-text-primary)" }}>{r.name}</div>
                    <div className="text-xs leading-tight mt-0.5" style={{ color: "var(--bw-text-secondary)" }}>{r.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-3">
                  {[0,1,2,3,4].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5" strokeWidth={0} fill="var(--bw-accent)" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--bw-text-primary)" }}>
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="pt-3 border-t text-xs flex items-center justify-between" style={{ borderColor: "var(--bw-border)" }}>
                  <span className="font-semibold" style={{ color: "var(--bw-text-secondary)" }}>{r.company}</span>
                  <span style={{ color: "var(--bw-brand)" }}>{r.project}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONCIERGE SERVICE */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)", backgroundColor: "var(--bw-text-primary)" }} data-testid="concierge-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 text-white">
            <div className="label-overline mb-4" style={{ color: "var(--bw-accent)" }}>NEW · Done-for-you</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Skip the paperwork.<br/>We'll handle everything.
            </h2>
            <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl">
              Introducing <strong className="text-white">BuildWise Concierge</strong> — our team of permit experts takes it from address to approval.
              We review the requirements, prepare every document, submit the applications, and liaise with your jurisdiction on your behalf.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: "Expert intake review", body: "A senior permit specialist audits your scope, address, and budget within 24 hours." },
                { title: "Full document prep", body: "Applications, site plans coordination, and every required form assembled correctly." },
                { title: "Application submission", body: "We submit to your jurisdiction and pay filing fees on your behalf (billed at cost)." },
                { title: "Dedicated project manager", body: "One human point of contact who tracks every step and communicates progress." },
              ].map((f) => (
                <div key={f.title} className="border-l-2 pl-4" style={{ borderColor: "var(--bw-accent)" }}>
                  <div className="font-semibold text-white">{f.title}</div>
                  <div className="text-sm text-white/70 mt-1">{f.body}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bw-card p-8" style={{ backgroundColor: "#ffffff" }}>
              <div className="label-overline" style={{ color: "var(--bw-accent)" }}>BuildWise Concierge</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="metric-big">$299</span>
                <span className="text-sm font-semibold" style={{ color: "var(--bw-text-tertiary)" }}>starting · per project</span>
              </div>
              <p className="mt-4 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
                Final price depends on project complexity and scope. You'll receive a firm quote after the free intake review — no obligation.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm" style={{ color: "var(--bw-text-primary)" }}>
                <li>✓ Everything in Professional Report</li>
                <li>✓ Human expert review (senior specialist)</li>
                <li>✓ Full document preparation & submission</li>
                <li>✓ Jurisdiction liaison & follow-ups</li>
                <li>✓ Dedicated project manager (email + phone)</li>
                <li>✓ Status updates until permit is issued</li>
              </ul>
              <Link to="/signup" className="btn-accent mt-8 w-full inline-flex justify-center" data-testid="concierge-cta">
                Request Concierge Intake
              </Link>
              <p className="mt-3 text-center text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
                Free intake review · Firm quote within 24 hours
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section style={{ backgroundColor: "var(--bw-brand)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 text-white">
          <div className="label-overline" style={{ color: "#7FB8D9" }}>San Diego Coverage</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-4 max-w-2xl">
            Every jurisdiction. From coast to backcountry.
          </h2>
          <div className="mt-10 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <span key={c} className="px-4 py-2 border border-white/30 text-sm font-medium" data-testid={`city-${c.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* OCEAN SECTION */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1519046904884-53103b34b206?w=2000&q=85&auto=format&fit=crop"
            alt="Pacific Ocean sunset"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=2000&q=85&auto=format&fit=crop"; }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(30,75,102,0.15) 0%, rgba(30,75,102,0.6) 60%, rgba(26,29,32,0.85) 100%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 py-32 md:py-48">
          <div className="max-w-2xl">
            <div className="label-overline mb-6" style={{ color: "#FFD9B8" }}>Built for the coast</div>
            <h2
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tight text-white leading-[1.05]"
              data-testid="ocean-headline"
            >
              From the Pacific<br/>to your permit.
            </h2>
            <p className="mt-8 text-lg md:text-xl text-white/90 leading-relaxed max-w-xl">
              We built BuildWise AI here in San Diego because we know how much of a difference local knowledge makes.
              From coastal setback rules to ADU state overlays, every answer is tuned to where you are.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2"><Waves className="w-4 h-4" strokeWidth={1.5} /> Coastal Commission aware</div>
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4" strokeWidth={1.5} /> Local code trained</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" strokeWidth={1.5} /> Real-time AI guidance</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-sm" style={{ color: "var(--bw-text-tertiary)" }}>
            © {new Date().getFullYear()} BuildWise AI. Informational only — not a substitute for professional advice.
          </div>
          <div className="text-sm font-semibold" style={{ color: "var(--bw-text-secondary)" }}>Know Before You Build.</div>
        </div>
      </footer>
    </div>
  );
}
